import { FC, memo } from "react";
import { useMediaQuery } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";
import { MessageSender } from "@/types/chat";
import { Typography } from "@/components";

/**
 * ChatBubble component renders chat messages with different styles:
 * - USER messages: Right-aligned chat bubbles with dark background
 * - AI messages: Left-aligned with full markdown support including:
 *   - Headers (h1, h2, h3)
 *   - Code blocks (inline and block)
 *   - Lists (ordered and unordered) 
 *   - Emphasis (bold, italic)
 *   - Blockquotes
 *   - Paragraphs with proper spacing
 */

interface ChatBubbleProps {
  message: string;
  sender: MessageSender;
}

const ChatBubble: FC<ChatBubbleProps> = memo(({ message, sender }) => {
  const isUser = sender === MessageSender.USER;
  const isShortHeight = useMediaQuery('(max-height:820px)');
  const isNarrowWidth = useMediaQuery('(max-width:1200px)');
  const isMobileWidth = useMediaQuery('(max-width:600px)');
  const isCompact = isShortHeight || isNarrowWidth;
  
  // Lightweight autolink: wrap bare URLs in angle brackets so ReactMarkdown treats them as links
  // Avoid adding a new dependency. This is conservative and good enough for our plain-text links.
  const linkifyAutolinks = (input: string): string => {
    if (!input) return input;
    // Replace bare http(s) URLs cautiously to avoid nested anchors:
    // - not immediately preceded by [, (, or <
    // - not immediately followed by ], ), or >
    // - not already inside angle brackets
    return input.replace(/(?<![\[(<])(https?:\/\/[^\s<>)\]]+)(?![\])>])/g, (_m, url) => `<${url}>`);
  };
  const renderedMessage = linkifyAutolinks(message);
  
  if (isUser) {
    // User messages: bubble style on the right
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: isCompact ? 14 : 17,
          width: "100%",
        }}
      >
        <div
          style={{
            maxWidth: isMobileWidth ? "85%" : "70%",
            padding: isCompact ? "10px 14px" : "12px 16px",
            borderRadius: "12px 12px 4px 12px",
            backgroundColor: "#E6ECED",
            wordWrap: "break-word",
            boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
          }}
        >
          <Typography
            variant="regular"
            fontSize={isCompact ? 16 : 18}
            color="#030712"
            style={{ lineHeight: 1.4, fontWeight: 560 }}
          >
            {message}
          </Typography>
        </div>
      </div>
    );
  } else {
    // AI messages: markdown-rendered text like ChatGPT
    return (
      <div
        style={{
          marginBottom: 16,
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
          // Enable text selection
          userSelect: 'text',
          WebkitUserSelect: 'text',
        }}
      >
        
        <div
          style={{
            color: "#030712",
            fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
            fontSize: isCompact ? 16 : 17,
            lineHeight: isCompact ? 1.55 : 1.6,
            letterSpacing: "0.003em",
            wordWrap: "break-word",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            textAlign: "left",
            maxWidth: "100%",
            overflow: "hidden",
            backgroundColor: "#FFFFFF",
            border: "1px solid #ebebeb",
            borderRadius: "10px",
            padding: isCompact ? "12px" : "14px",
            boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
            // Enable text selection
            userSelect: 'text',
            WebkitUserSelect: 'text',
            cursor: 'text',
          }}
        >
          {(() => {
            const markdownComponents: Components = {
              // Links: open in new tab, safe rel
              a: ({ href, children }) => (
                <a
                  href={href || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#295BFF', 
                    textDecoration: 'underline', 
                    wordBreak: 'break-all',
                    cursor: 'pointer',
                    // Ensure links are clickable above any overlays
                    position: 'relative',
                    zIndex: 1,
                  }}
                  onMouseDown={(e) => {
                    // Stop propagation on mousedown to prevent any parent handlers
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    // Ensure link clicks are not prevented by parent handlers
                    e.stopPropagation();
                    // Let the browser handle the link navigation
                  }}
                >
                  {children}
                </a>
              ),
              // Style headings
              h1: ({ children }) => (
                <h1 style={{ fontSize: isCompact ? 20 : 22, fontWeight: 600, margin: isCompact ? '12px 0 6px 0' : '16px 0 8px 0', color: '#030712' }}>
                  {children}
                </h1>
              ),
              h2: ({ children }) => (
                <h2 style={{ fontSize: isCompact ? 18 : 20, fontWeight: 600, margin: isCompact ? '10px 0 6px 0' : '14px 0 7px 0', color: '#030712' }}>
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 style={{ fontSize: isCompact ? 16 : 18, fontWeight: 600, margin: isCompact ? '8px 0 5px 0' : '12px 0 6px 0', color: '#030712' }}>
                  {children}
                </h3>
              ),
              // Style code blocks - handle both inline and block code
              code: (props) => {
                const { children, ...rest } = props as unknown as { children?: React.ReactNode; inline?: boolean; className?: string } & Record<string, unknown>;
                const inline = (props as unknown as { inline?: boolean }).inline;
                const className = (props as unknown as { className?: string }).className;
                
                // Skip rendering ```ui code blocks - they're handled by GenerativeUIBlock component
                if (!inline && className?.includes('language-ui')) {
                  return null;
                }
                
                return inline ? (
                  <code
                    style={{
                      backgroundColor: '#f7f8f8',
                      padding: isCompact ? '1px 3px' : '2px 4px',
                      borderRadius: '3px',
                      fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      fontSize: isCompact ? 13 : 14,
                      color: '#116B6F',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      maxWidth: '100%',
                    }}
                    {...(rest as Record<string, unknown>)}
                  >
                    {children}
                  </code>
                ) : (
                  // For block code, return just the code element - ReactMarkdown will wrap it in pre
                  <code
                    style={{
                      fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      fontSize: isCompact ? 13 : 14,
                      color: '#030712',
                      display: 'block',
                      backgroundColor: '#f7f8f8',
                      padding: isCompact ? '10px' : '12px',
                      borderRadius: '6px',
                      border: '1px solid #ebebeb',
                      overflow: 'auto',
                      overflowX: 'auto',
                      overflowY: 'hidden',
                      margin: isCompact ? '6px 0' : '8px 0',
                      maxWidth: '100%',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                    {...(rest as Record<string, unknown>)}
                  >
                    {children}
                  </code>
                );
              },
              // Override pre to prevent nesting issues - ReactMarkdown wraps code blocks in pre
              pre: ({ children }) => {
                // If children is a code element, render it directly without wrapping
                const child = children as any;
                if (child?.props?.className) {
                  // This is a code block, render it directly
                  return <>{children}</>;
                }
                // Otherwise, render as normal pre
                return (
                  <pre
                    style={{
                      backgroundColor: '#f7f8f8',
                      padding: isCompact ? '10px' : '12px',
                      borderRadius: '6px',
                      border: '1px solid #ebebeb',
                      overflow: 'auto',
                      margin: isCompact ? '6px 0' : '8px 0',
                      maxWidth: '100%',
                    }}
                  >
                    {children}
                  </pre>
                );
              },
              // Style lists
              ul: ({ children }) => (
                <ul style={{ margin: isCompact ? '6px 0' : '8px 0', paddingLeft: isCompact ? '18px' : '20px' }}>
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol style={{ margin: isCompact ? '6px 0' : '8px 0', paddingLeft: isCompact ? '18px' : '20px' }}>
                  {children}
                </ol>
              ),
              li: ({ children }) => (
                <li style={{ margin: isCompact ? '3px 0' : '4px 0' }}>
                  {children}
                </li>
              ),
              // Style paragraphs
              p: ({ children }) => (
                <p style={{ margin: isCompact ? '6px 0' : '8px 0', lineHeight: isCompact ? 1.55 : 1.6 }}>
                  {children}
                </p>
              ),
              // Style emphasis
              strong: ({ children }) => (
                <strong style={{ fontWeight: 600, color: '#030712' }}>
                  {children}
                </strong>
              ),
              em: ({ children }) => (
                <em style={{ fontStyle: 'italic' }}>
                  {children}
                </em>
              ),
              // Style blockquotes
              blockquote: ({ children }) => (
                <blockquote
                  style={{
                    borderLeft: '4px solid #ddd',
                    paddingLeft: isCompact ? '12px' : '16px',
                    margin: isCompact ? '6px 0' : '8px 0',
                    color: '#666',
                    fontStyle: 'italic',
                  }}
                >
                  {children}
                </blockquote>
              ),
              // Style tables (from remark-gfm)
              table: ({ children }) => (
                <div style={{ 
                  overflowX: 'auto', 
                  margin: isCompact ? '8px 0' : '12px 0',
                  width: '100%',
                  maxWidth: '100%',
                }}>
                  <table
                    style={{
                      borderCollapse: 'collapse',
                      width: '100%',
                      border: '1px solid #e0e0e0',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      fontSize: isCompact ? 14 : 15,
                    }}
                  >
                    {children}
                  </table>
                </div>
              ),
              thead: ({ children }) => (
                <thead
                  style={{
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #e0e0e0',
                  }}
                >
                  {children}
                </thead>
              ),
              tbody: ({ children }) => (
                <tbody>
                  {children}
                </tbody>
              ),
              tr: ({ children }) => (
                <tr
                  style={{
                    borderBottom: '1px solid #e0e0e0',
                  }}
                >
                  {children}
                </tr>
              ),
              th: ({ children }) => (
                <th
                  style={{
                    padding: isCompact ? '8px 10px' : '10px 12px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: '#030712',
                    borderRight: '1px solid #e0e0e0',
                  }}
                >
                  {children}
                </th>
              ),
              td: ({ children }) => (
                <td
                  style={{
                    padding: isCompact ? '8px 10px' : '10px 12px',
                    borderRight: '1px solid #e0e0e0',
                    color: '#030712',
                  }}
                >
                  {children}
                </td>
              ),
            };

            return (
              <ReactMarkdown 
                components={markdownComponents}
                remarkPlugins={[remarkGfm]}
              >
                {renderedMessage}
              </ReactMarkdown>
            );
          })()}
        </div>
      </div>
    );
  }
});

// Set display name for debugging
ChatBubble.displayName = 'ChatBubble';

export default ChatBubble;
